// frontend/src/utils/rosterParser.js
// Parses the two roster spreadsheet formats teams submit into one normalized shape.
//
//   Team LongList      — S.No | Name | Date of Birth | Passport Number | Designation
//   Accreditation List — Family Name | Given Name | Date of Birth | Designation
//
// Both arrive as .xlsx with banner rows, logos and merged cells above the real header,
// so the header row is found by content rather than assumed to be row 0.
//
// Input is a plain 2D array of cell values, which keeps this module independent of
// whichever spreadsheet reader supplies it (and trivially testable).
//
// Passport numbers are deliberately NOT read. They are high-sensitivity PII, date of
// birth already does the disambiguation work, and data never parsed cannot leak.

const HEADER_SCAN_DEPTH = 20;

// Recognised spellings for each field we care about. Matched after normalizeHeader().
const FIELD_TOKENS = {
    familyName: ['family name', 'familyname', 'surname', 'last name', 'lastname'],
    givenName: ['given name', 'givenname', 'first name', 'firstname', 'forename'],
    fullName: ['name', 'full name', 'fullname', 'player name', 'name of player', 'athlete name'],
    dob: ['date of birth', 'dateofbirth', 'dob', 'd o b', 'birth date', 'birthdate', 'born'],
    designation: ['designation', 'role', 'position', 'function', 'category'],
};

// Present only so a header row containing them still scores as a header.
// The values are never read into the output.
const IGNORED_TOKENS = ['s no', 'sno', 'sr no', 'serial', 'no', '#', 'passport', 'passport number', 'passport no'];

const MONTHS = {
    jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3, apr: 4, april: 4,
    may: 5, jun: 6, june: 6, jul: 7, july: 7, aug: 8, august: 8, sep: 9, sept: 9,
    september: 9, oct: 10, october: 10, nov: 11, november: 11, dec: 12, december: 12,
};

/**
 * Cell value → trimmed string. Handles the numbers, dates, nulls and wrapper objects a
 * sheet yields. Rich text must be flattened rather than ignored: a team that bolds or
 * colours part of a name would otherwise have that player silently dropped.
 */
const cellText = (value) => {
    if (value === null || value === undefined) return '';
    if (value instanceof Date) return value.toISOString();
    if (typeof value === 'object') {
        if (Array.isArray(value.richText)) return value.richText.map((run) => run.text || '').join('').trim();
        if (value.formula !== undefined) return cellText(value.result);
        if (value.text !== undefined) return String(value.text).trim();
        return '';
    }
    return String(value).trim();
};

/** Lowercase, strip punctuation and collapse whitespace so header spellings converge. */
const normalizeHeader = (value) =>
    cellText(value)
        .toLowerCase()
        .replace(/[._:*()[\]]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

const pad2 = (n) => String(n).padStart(2, '0');
const toIso = (y, m, d) => `${y}-${pad2(m)}-${pad2(d)}`;

const isRealDate = (y, m, d) => {
    if (m < 1 || m > 12 || d < 1 || d > 31) return false;
    const probe = new Date(Date.UTC(y, m - 1, d));
    return probe.getUTCFullYear() === y && probe.getUTCMonth() === m - 1 && probe.getUTCDate() === d;
};

// Excel counts days from an epoch of 1899-12-30 once its fictitious 1900 leap day is
// accounted for. Every date of birth we handle falls after 1900-03-01, where that
// offset is uniformly correct.
const fromExcelSerial = (serial) => {
    const utc = new Date(Date.UTC(1899, 11, 30) + Math.round(serial) * 86400000);
    return toIso(utc.getUTCFullYear(), utc.getUTCMonth() + 1, utc.getUTCDate());
};

const NUMERIC_DATE = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})$/;

/**
 * Work out whether a file writes dates day-first or month-first.
 *
 * A single row is often undecidable — 06.01.1996 could be either — but a roster is not.
 * Any date whose first part exceeds 12 settles the whole file, and one such row is enough
 * to read every other date with confidence. Fifty players almost always include someone
 * born after the 12th.
 *
 * Returns 'dmy', 'mdy', or null when the file offers no evidence or contradicts itself.
 */
export const inferDateOrder = (values) => {
    let dayFirst = 0;
    let monthFirst = 0;

    for (const value of values) {
        const match = cellText(value).match(NUMERIC_DATE);
        if (!match) continue;
        const a = Number(match[1]);
        const b = Number(match[2]);
        if (a > 12 && b <= 12) dayFirst++;
        else if (b > 12 && a <= 12) monthFirst++;
    }

    if (dayFirst && !monthFirst) return 'dmy';
    if (monthFirst && !dayFirst) return 'mdy';
    return null;
};

/**
 * Normalize any spreadsheet date-of-birth representation to an ISO date.
 *
 * Returns { iso, ambiguous, warning }. `order` is the file-wide reading established by
 * inferDateOrder; when it is known, a date that would otherwise be undecidable is read
 * with it and not flagged. Without it, day-first is assumed and the row is flagged.
 */
export const normalizeDob = (value, order = null) => {
    const miss = (warning) => ({ iso: null, ambiguous: false, warning: warning || null });

    if (value === null || value === undefined || value === '') return miss(null);

    if (value instanceof Date && !isNaN(value.getTime())) {
        return { iso: toIso(value.getUTCFullYear(), value.getUTCMonth() + 1, value.getUTCDate()), ambiguous: false, warning: null };
    }

    if (typeof value === 'number' && isFinite(value)) {
        if (value <= 0) return miss('Unreadable date');
        return { iso: fromExcelSerial(value), ambiguous: false, warning: null };
    }

    const text = cellText(value);
    if (!text) return miss(null);

    // A bare number that arrived as text is still an Excel serial.
    if (/^\d{5}$/.test(text)) return { iso: fromExcelSerial(Number(text)), ambiguous: false, warning: null };

    // ISO, unambiguous by definition.
    const iso = text.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/);
    if (iso) {
        const [, y, m, d] = iso.map(Number);
        return isRealDate(y, m, d)
            ? { iso: toIso(y, m, d), ambiguous: false, warning: null }
            : miss(`Unreadable date "${text}"`);
    }

    // "14 Mar 1998" / "Mar 14, 1998"
    const named = text.match(/^(\d{1,2})\s+([a-z]+)\.?\s+(\d{4})$/i) || text.match(/^([a-z]+)\.?\s+(\d{1,2}),?\s+(\d{4})$/i);
    if (named) {
        const dayFirst = /^\d/.test(named[1]);
        const day = Number(dayFirst ? named[1] : named[2]);
        const month = MONTHS[(dayFirst ? named[2] : named[1]).toLowerCase()];
        const year = Number(named[3]);
        if (month && isRealDate(year, month, day)) {
            return { iso: toIso(year, month, day), ambiguous: false, warning: null };
        }
        return miss(`Unreadable date "${text}"`);
    }

    // a/b/yyyy — whichever part exceeds 12 must be the day. If neither does, the file's
    // own convention decides; failing that it is genuinely undecidable.
    const numeric = text.match(NUMERIC_DATE);
    if (numeric) {
        const a = Number(numeric[1]);
        const b = Number(numeric[2]);
        let year = Number(numeric[3]);
        if (numeric[3].length === 2) year += year > 30 ? 1900 : 2000;

        let day, month, ambiguous = false;
        if (a > 12 && b <= 12) {
            day = a; month = b;
        } else if (b > 12 && a <= 12) {
            day = b; month = a;
        } else if (order === 'mdy') {
            day = b; month = a;
        } else if (order === 'dmy') {
            day = a; month = b;
        } else {
            // No evidence either way. Day-first for an international federation, and
            // flagged — unless the two parts are equal, where the reading cannot differ.
            day = a; month = b;
            ambiguous = a !== b;
        }

        if (!isRealDate(year, month, day)) return miss(`Unreadable date "${text}"`);
        return {
            iso: toIso(year, month, day),
            ambiguous,
            warning: ambiguous ? `"${text}" could also be ${toIso(year, day, month)}` : null,
        };
    }

    return miss(`Unreadable date "${text}"`);
};

/**
 * Locate the header row by content, skipping banners and blank spacer rows.
 * Returns { index, columns } for the best-scoring row, or null if none qualifies.
 */
export const findHeaderRow = (matrix, maxScan = HEADER_SCAN_DEPTH) => {
    const known = [...Object.values(FIELD_TOKENS).flat(), ...IGNORED_TOKENS];
    let best = null;

    const depth = Math.min(matrix.length, maxScan);
    for (let i = 0; i < depth; i++) {
        const columns = (matrix[i] || []).map(normalizeHeader);
        const score = columns.filter((c) => c && known.includes(c)).length;
        if (score >= 2 && (!best || score > best.score)) best = { index: i, columns, score };
    }

    return best ? { index: best.index, columns: best.columns } : null;
};

/** Index of the first column whose header matches one of `tokens`, or -1. */
const columnFor = (columns, tokens) => columns.findIndex((c) => tokens.includes(c));

/**
 * Which of the two formats a header row represents.
 * Split family/given columns are the distinguishing feature of the accreditation list.
 */
export const detectFormat = (columns) => {
    const hasFamily = columnFor(columns, FIELD_TOKENS.familyName) !== -1;
    const hasGiven = columnFor(columns, FIELD_TOKENS.givenName) !== -1;
    if (hasFamily && hasGiven) return 'accreditation';
    if (columnFor(columns, FIELD_TOKENS.fullName) !== -1) return 'longlist';
    return null;
};

/**
 * Both orderings of a split name, plus the raw full name. Sportradar's
 * name_full_latin does not guarantee an ordering, so matching scores against each
 * and keeps the best.
 */
const buildNameCandidates = ({ fullName, familyName, givenName }) => {
    const out = [];
    const push = (v) => {
        const t = (v || '').replace(/\s+/g, ' ').trim();
        if (t && !out.includes(t)) out.push(t);
    };
    push(fullName);
    if (givenName && familyName) {
        push(`${givenName} ${familyName}`);
        push(`${familyName} ${givenName}`);
    } else {
        push(givenName);
        push(familyName);
        // A longlist gives one undifferentiated name. Two tokens can be safely swapped;
        // three or more cannot be split into given/family without guessing.
        const tokens = (fullName || '').split(/\s+/).filter(Boolean);
        if (tokens.length === 2) push(`${tokens[1]} ${tokens[0]}`);
    }
    return out;
};

/**
 * Parse a spreadsheet matrix into normalized roster entries.
 *
 * Returns { format, headerRow, entries, warnings, skippedRows }. Rows without a usable
 * name are skipped rather than imported as blanks. Designation is carried through for
 * on-screen context only — it is not persisted.
 *
 * Only the header row and the rows beneath it are read. The banner above it is located
 * and skipped, never interpreted.
 */
export const parseRoster = (matrix) => {
    const warnings = [];
    const empty = { format: null, headerRow: -1, entries: [], warnings, skippedRows: 0 };

    if (!Array.isArray(matrix) || matrix.length === 0) {
        warnings.push('The file appears to be empty.');
        return empty;
    }

    const header = findHeaderRow(matrix);
    if (!header) {
        warnings.push('Could not find a header row. Expected columns such as "Name" or "Family Name" plus "Date of Birth".');
        return empty;
    }

    const format = detectFormat(header.columns);
    if (!format) {
        warnings.push('Found a header row but no name column in it.');
        return { ...empty, headerRow: header.index };
    }

    const idx = {
        family: columnFor(header.columns, FIELD_TOKENS.familyName),
        given: columnFor(header.columns, FIELD_TOKENS.givenName),
        full: columnFor(header.columns, FIELD_TOKENS.fullName),
        dob: columnFor(header.columns, FIELD_TOKENS.dob),
        designation: columnFor(header.columns, FIELD_TOKENS.designation),
    };

    if (idx.dob === -1) {
        warnings.push('No date-of-birth column found. Matching will rely on names alone and will be far less reliable.');
    }

    const at = (row, i) => (i >= 0 ? cellText(row[i]) : '');
    const entries = [];
    let skippedRows = 0;

    // Settle the file's date convention before reading any row, so an undecidable date is
    // resolved by its neighbours rather than flagged on its own.
    const dateOrder = idx.dob >= 0
        ? inferDateOrder(matrix.slice(header.index + 1).map((row) => (row || [])[idx.dob]))
        : null;
    const ambiguousDates = [];

    for (let r = header.index + 1; r < matrix.length; r++) {
        const row = matrix[r] || [];
        if (row.every((cell) => cellText(cell) === '')) continue; // spacer

        const familyName = at(row, idx.family);
        const givenName = at(row, idx.given);
        const explicitFull = at(row, idx.full);
        const fullName = explicitFull || [givenName, familyName].filter(Boolean).join(' ').trim();

        if (!fullName) {
            skippedRows++;
            continue;
        }

        // A cell merged across the sheet width reports its value in every column it
        // covers, so a footer like "Deadline: 20 May 2026" arrives looking like a row
        // whose name, date and designation are all the same string. Real people never
        // repeat one value across two mapped columns.
        const nameText = explicitFull || familyName || givenName;
        const mapped = [idx.family, idx.given, idx.full, idx.dob, idx.designation]
            .filter((i) => i >= 0)
            .map((i) => cellText(row[i]));
        if (mapped.filter((text) => text === nameText).length >= 2) continue;

        const raw = idx.dob >= 0 ? row[idx.dob] : null;
        const dob = normalizeDob(raw, dateOrder);
        if (dob.ambiguous) ambiguousDates.push(`row ${r + 1} ${cellText(raw)}`);
        else if (dob.warning) warnings.push(`Row ${r + 1} (${fullName}): ${dob.warning}`);

        entries.push({
            sourceRow: r + 1, // 1-based, so it lines up with what the user sees in Excel
            fullName,
            familyName: familyName || null,
            givenName: givenName || null,
            nameCandidates: buildNameCandidates({ fullName: explicitFull, familyName, givenName }),
            dob: dob.iso,
            dobAmbiguous: dob.ambiguous,
            designation: at(row, idx.designation) || null,
        });
    }

    // One line rather than one per row. The rows themselves are marked, and 25 near
    // identical warnings bury the ones that matter.
    if (ambiguousDates.length) {
        const sample = ambiguousDates.slice(0, 3).join(', ');
        warnings.push(
            `${ambiguousDates.length} ${ambiguousDates.length === 1 ? 'date is' : 'dates are'} ` +
            `written day.month.year or month.day.year and this file does not settle which ` +
            `(${sample}${ambiguousDates.length > 3 ? ', …' : ''}). Read as day first — check the rows marked ?.`
        );
    }

    if (entries.length === 0) warnings.push('No usable rows found below the header.');

    return { format, headerRow: header.index, entries, warnings, skippedRows };
};
